import { Info } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';

import CustomButton from '../../designSystem/Button';
import { LightTooltip } from '../../designSystem/WhiteTooltip';
import { handleExportGenericPDF } from './banks/0';
import { handleExportBancoDeChileXLSX } from './banks/1';
import { handleExportBICECsv } from './banks/28';
import { handleExportSantanderXLSX } from './banks/37';
import { handleExportItauTxt } from './banks/39';
import { handleExportConsorcioXLSX } from './banks/55';
import { ExportBankFunction, Payment } from './types';

const BANKS_WITH_NOMINEE_EXPORTER: Record<
  number,
  { name: string; export: ExportBankFunction }
> = {
  1: { name: 'Banco de Chile', export: handleExportBancoDeChileXLSX },
  28: { name: 'Banco BICE', export: handleExportBICECsv },
  37: { name: 'Banco Santander', export: handleExportSantanderXLSX },
  39: { name: 'Banco Itaú', export: handleExportItauTxt },
  55: { name: 'Banco Consorcio', export: handleExportConsorcioXLSX },
};

const EXPORT_OPTION_PDF = 'pdf';
const EXPORT_OPTION_MULTINOMINAS = 'multinominas';

type ExportOption =
  | typeof EXPORT_OPTION_PDF
  | typeof EXPORT_OPTION_MULTINOMINAS;

type NomineeExporterProps = {
  payments: Payment[];
  open: boolean;
  setOpen: (open: boolean) => void;
  id?: string;
};

const defaultGlosa = (id?: string) => `Operación ${id || ''}`;

const NomineeExporter = (props: NomineeExporterProps) => {
  const { payments, open, setOpen, id } = props;
  const [exportOption, setExportOption] =
    useState<ExportOption>(EXPORT_OPTION_PDF);
  const [glosasByBank, setGlosasByBank] = useState<Record<number, string>>({});

  const {
    everyPaymentHaveExporter,
    bankNamesWithoutExporter,
    banksInPayments,
  } = useMemo(() => {
    const bankCodesInPayments = [
      ...new Set(payments.map((p) => p.bankAccountOriginBankCode)),
    ];
    const withExporter = bankCodesInPayments.filter((code) =>
      Object.keys(BANKS_WITH_NOMINEE_EXPORTER).includes(String(code)),
    );
    const withoutExporter = bankCodesInPayments.filter(
      (code) =>
        !Object.keys(BANKS_WITH_NOMINEE_EXPORTER).includes(String(code)),
    );
    const bankNamesWithoutExporter = [
      ...new Set(
        payments
          .filter((p) => withoutExporter.includes(p.bankAccountOriginBankCode))
          .map((p) => p.bankAccountOriginName),
      ),
    ];
    return {
      everyPaymentHaveExporter:
        withExporter.length === bankCodesInPayments.length,
      bankNamesWithoutExporter,
      banksInPayments: withExporter,
    };
  }, [payments]);

  const handleExport = () => {
    if (exportOption === EXPORT_OPTION_PDF) {
      void handleExportGenericPDF(payments, id || '');
      return;
    }
    const paymentsByBank = payments.reduce<Record<number, Payment[]>>(
      (acc, payment) => {
        const code = payment.bankAccountOriginBankCode;
        if (!acc[code]) acc[code] = [];
        acc[code].push(payment);
        return acc;
      },
      {},
    );
    Object.entries(paymentsByBank).forEach(([codeStr, bankPayments]) => {
      const code = Number(codeStr);
      const bank = BANKS_WITH_NOMINEE_EXPORTER[code];
      if (bank) {
        const glosa = glosasByBank[code] ?? defaultGlosa(id);
        void bank.export(bankPayments, id || '', glosa);
      }
    });
  };

  const selectOptions: { value: ExportOption; label: string }[] =
    everyPaymentHaveExporter
      ? [
          { value: EXPORT_OPTION_PDF, label: 'PDF genérico' },
          {
            value: EXPORT_OPTION_MULTINOMINAS,
            label: 'Multinóminas bancarias',
          },
        ]
      : [{ value: EXPORT_OPTION_PDF, label: 'PDF genérico' }];

  return (
    <div>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle>Exportar</DialogTitle>
        <DialogContent>
          {everyPaymentHaveExporter ? (
            <>
              <Typography variant="body1" gutterBottom>
                Bancos en esta operación con exportador de nómina:{' '}
                {banksInPayments
                  .map(
                    (code) => BANKS_WITH_NOMINEE_EXPORTER[code]?.name ?? code,
                  )
                  .join(', ')}
                <LightTooltip
                  title={
                    <Typography variant="body2" sx={{ p: 1 }}>
                      Al elegir &quot;Multinóminas bancarias&quot;, se generarán
                      automáticamente los archivos de nómina correspondientes a
                      cada banco (uno por banco presente en los pagos).
                    </Typography>
                  }
                >
                  <Info
                    sx={{
                      fontSize: 18,
                      verticalAlign: 'middle',
                      marginLeft: 0.5,
                      cursor: 'help',
                    }}
                  />
                </LightTooltip>
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Algunos pagos tienen banco de origen sin exportador de nómina (
              <strong>{bankNamesWithoutExporter.join(', ')}</strong>). Solo está
              disponible la exportación a PDF genérico.
            </Typography>
          )}

          <FormControl
            variant="outlined"
            fullWidth
            style={{ marginTop: 16, marginBottom: 10 }}
          >
            <InputLabel id="export-type">Tipo de exportación</InputLabel>
            <Select
              labelId="export-type"
              fullWidth
              value={
                everyPaymentHaveExporter ? exportOption : EXPORT_OPTION_PDF
              }
              onChange={(e) => setExportOption(e.target.value as ExportOption)}
              label="Tipo de exportación"
            >
              {selectOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {exportOption === EXPORT_OPTION_MULTINOMINAS && (
            <div style={{ marginTop: 16 }}>
              <Typography variant="subtitle2" gutterBottom>
                Glosa por banco
              </Typography>
              {banksInPayments.map((code) => (
                <TextField
                  key={code}
                  label={
                    BANKS_WITH_NOMINEE_EXPORTER[code]?.name ?? `Banco ${code}`
                  }
                  variant="outlined"
                  fullWidth
                  value={glosasByBank[code] ?? defaultGlosa(id)}
                  onChange={(e) =>
                    setGlosasByBank((prev) => ({
                      ...prev,
                      [code]: e.target.value,
                    }))
                  }
                  style={{ marginBottom: 10 }}
                  placeholder={defaultGlosa(id)}
                />
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton color="secondary" onClick={() => setOpen(false)}>
            Cancelar
          </CustomButton>
          <CustomButton
            onClick={() => handleExport()}
            variant="contained"
            color="primary"
            size="small"
          >
            Exportar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default NomineeExporter;
